<script setup>
import { computed } from 'vue';

const props = defineProps({
  modelValue: {
    type: [Number, String],
    default: 0,
  },
  min: {
    type: Number,
    default: 0,
  },
  max: {
    type: Number,
    default: 100,
  },
  step: {
    type: Number,
    default: 1,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);

const numericValue = computed(() =>
  typeof props.modelValue === 'string'
    ? parseFloat(props.modelValue)
    : props.modelValue
);

const value = computed({
  get: () => numericValue.value,
  set: val => emit('update:modelValue', Number(val)),
});

// Calculate progress percentage for styling
const progressPercent = computed(() => {
  const range = props.max - props.min;
  const progress = ((numericValue.value - props.min) / range) * 100;
  return Math.min(Math.max(progress, 0), 100);
});
</script>

<template>
  <div class="relative w-full py-2">
    <!-- Track container -->
    <div class="relative h-2 rounded-full bg-n-slate-4">
      <!-- Active track -->
      <div
        class="absolute inset-y-0 left-0 rounded-full bg-n-blue-9"
        :style="{ width: `${progressPercent}%` }"
      />
    </div>
    <!-- Input overlaid -->
    <input
      v-model="value"
      type="range"
      :min="min"
      :max="max"
      :step="step"
      :disabled="disabled"
      class="absolute inset-0 w-full appearance-none cursor-pointer bg-transparent focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed slider-input"
    />
  </div>
</template>

<style>
/* Custom slider thumb styles - uses CSS variables for theming */
.slider-input::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: rgb(var(--blue-9));
  border: 2px solid rgb(var(--solid-1));
  cursor: pointer;
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.15),
    0 1px 2px rgba(0, 0, 0, 0.1);
  transition: all 0.15s ease;
  margin-top: 0;
}

.slider-input::-webkit-slider-thumb:hover {
  transform: scale(1.1);
  background: rgb(var(--blue-10));
  box-shadow:
    0 2px 4px rgba(0, 0, 0, 0.2),
    0 1px 2px rgba(0, 0, 0, 0.1);
}

.slider-input::-webkit-slider-thumb:active {
  transform: scale(0.95);
}

.slider-input::-moz-range-thumb {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: rgb(var(--blue-9));
  border: 2px solid rgb(var(--solid-1));
  cursor: pointer;
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.15),
    0 1px 2px rgba(0, 0, 0, 0.1);
  transition: all 0.15s ease;
}

.slider-input::-moz-range-thumb:hover {
  transform: scale(1.1);
  background: rgb(var(--blue-10));
}

/* Track styles - hide default track */
.slider-input::-webkit-slider-runnable-track {
  height: 100%;
  background: transparent;
}

.slider-input::-moz-range-track {
  height: 100%;
  background: transparent;
}
</style>
